import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {EventResponse, EventClassResponse, EventServiceResponse, EventSponsorResponse, EventContactResponse, EventScheduleResponse, EventScheduleEntryResponse, EventAnnouncementResponse, TrainingPackageResponse, TrainingSessionResponse, TrainingSessionAssignmentResponse, PublicClassResultsResponse, PublicResultResponse, PublicRoundResultResponse, PriceInfoResponse, UserPricesResponse} from '../../@shared/models/response.module';
import {MembershipPurchaseModal} from '../../@shared/components/membership-purchase-modal/membership-purchase-modal';
import {EventsService} from '../../@shared/services/events-service';
import {TrainingService} from '../../@shared/services/training.service';
import {GeocodingService} from '../../@shared/services/geocoding.service';
import {ScoringService} from '../../@shared/services/scoring.service';
import {MapMarker, MapConfig} from '../../@shared/components/location-map/location-map';
import {AQH_EVE_53} from '../../@shared/models/input.module';
import {formatDateDMYDots} from '../../@core/utils/utils.service';
import {interval, Subscription} from 'rxjs';
import {SendMessageModalComponent} from '../../@shared/components/send-message-modal/send-message-modal.component';
import {DocumentViewerModal} from '../../@shared/components/document-viewer-modal/document-viewer-modal';
import {StorageService} from '../../@core/services/storage.service';
import {TranslateService} from '@ngx-translate/core';
import {AnalyticsService} from '../../@core/services/analytics.service';

@Component({
  selector: 'app-event-details',
  standalone: false,
  templateUrl: './event-details.html',
  styleUrl: './event-details.css'
})
export class EventDetails implements OnInit, OnDestroy {
  eventId: number;
  event: EventResponse | null = null;
  eventClasses: EventClassResponse[] = [];
  eventServices: EventServiceResponse[] = [];
  eventSponsors: EventSponsorResponse[] = [];
  eventContacts: EventContactResponse[] = [];
  eventSchedules: EventScheduleResponse[] = [];
  announcements: EventAnnouncementResponse[] = [];
  trainingPackages: TrainingPackageResponse[] = [];
  trainingSessions: TrainingSessionResponse[] = [];
  trainingSessionAssignments: TrainingSessionAssignmentResponse[] = [];

  loading: boolean = false;
  loadingClasses: boolean = false;
  loadingServices: boolean = false;
  loadingSponsors: boolean = false;
  loadingContacts: boolean = false;
  loadingSchedules: boolean = false;
  loadingAnnouncements: boolean = false;
  loadingPackages: boolean = false;
  loadingSessions: boolean = false;

  // View toggle (information vs announcements vs results)
  currentView: 'information' | 'announcements' | 'results' = 'information';

  // Description expand/collapse
  descriptionExpanded: boolean = false;

  // Lightbox for announcement images
  lightboxImage: string | null = null;

  // Section navigation
  activeSection: string = 'overview';
  private sectionIds = ['overview', 'classes', 'packages', 'training-schedule', 'services', 'schedules', 'live', 'contacts'];

  // Schedule refresh subscription
  private scheduleRefreshSub: Subscription | null = null;
  private readonly REFRESH_INTERVAL = 30000; // 30 seconds

  // Track expanded groups for mobile view
  private expandedGroups = new Set<string>();

  // Track expanded training sessions
  private expandedTrainingSessions = new Set<number>();

  // Results view
  classResults: PublicClassResultsResponse[] = [];
  loadingResults: boolean = false;
  expandedClassResults = new Set<number>();

  // Map properties
  mapMarker: MapMarker | null = null;
  mapConfig: MapConfig = {
    height: '200px',
    mobileHeight: '180px',
    enableClustering: false,
    defaultZoom: 14,
    directionsMode: true
  };
  geocodingInProgress: boolean = false;

  // Membership pricing
  priceInfoMap: Map<string, PriceInfoResponse> = new Map();
  userPlanIds: number[] = [];
  loadingPrices: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventsService: EventsService,
    private trainingService: TrainingService,
    private geocodingService: GeocodingService,
    private scoringService: ScoringService,
    private modalService: NgbModal,
    private storageService: StorageService,
    private translateService: TranslateService,
    private analyticsService: AnalyticsService
  ) {}

  get isTrainingEvent(): boolean {
    return this.event?.category === 'training';
  }

  get isSocialEvent(): boolean {
    return this.event?.category === 'social';
  }

  get isCompetitionEvent(): boolean {
    return this.event?.category === 'competition' || !this.event?.category;
  }

  get isLoggedIn(): boolean {
    return this.storageService.isLoggedIn();
  }

  get isRegistrationClosed(): boolean {
    if (!this.event?.registration_deadline) {
      return false;
    }
    const deadline = new Date(this.event.registration_deadline);
    const now = new Date();
    return now > deadline;
  }

  ngOnInit() {
    // Get event ID from route params
    this.route.params.subscribe(params => {
      this.eventId = +params['id'];
      if (this.eventId) {
        this.loadEventDetails();
        this.loadEventClasses();
        this.loadEventServices();
        this.loadEventSponsors();
        this.loadEventContacts();
        this.loadPublicSchedules();
        this.loadAnnouncements();
        this.startScheduleRefresh();
        // Load membership pricing if logged in
        if (this.isLoggedIn) {
          this.loadUserPrices();
        }
      }
    });

    // Check for view query parameter to set initial view
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['view']) {
        const view = queryParams['view'];
        if (view === 'results' || view === 'announcements' || view === 'information') {
          this.currentView = view;
          if (view === 'results') {
            this.loadResults();
          }
        }
      }
    });
  }

  ngOnDestroy() {
    if (this.scheduleRefreshSub) {
      this.scheduleRefreshSub.unsubscribe();
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.updateActiveSection();
  }

  updateActiveSection() {
    const navHeight = 70; // Height of sticky nav
    const scrollPosition = window.scrollY + navHeight + 50;

    for (const sectionId of [...this.sectionIds].reverse()) {
      const element = document.getElementById(sectionId);
      if (element) {
        const offsetTop = element.offsetTop;
        if (scrollPosition >= offsetTop) {
          this.activeSection = sectionId;
          return;
        }
      }
    }
    this.activeSection = 'overview';
  }

  scrollToSection(sectionId: string) {
    const element = document.getElementById(sectionId);
    if (element) {
      const navHeight = 70; // Height of sticky nav
      const offsetTop = element.offsetTop - navHeight - 20;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      this.activeSection = sectionId;
    }
  }

  startScheduleRefresh() {
    // Refresh schedules every 30 seconds for live updates
    this.scheduleRefreshSub = interval(this.REFRESH_INTERVAL).subscribe(() => {
      this.loadPublicSchedules(true); // Pass true for refresh to avoid UI flash
    });
  }

  loadEventDetails() {
    this.loading = true;

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEvent(this.eventId).subscribe({
      next: (response) => {
        if (response.data && response.data.length > 0) {
          this.event = response.data[0];
          this.geocodeEventAddress();

          // Track event view with Google Analytics
          this.analyticsService.trackEventView(
            this.eventId,
            this.event.event_name || 'Unknown Event',
            this.event.category || 'event'
          );

          // Load packages and sessions for training events
          if (this.isTrainingEvent) {
            this.loadTrainingPackages();
            this.loadTrainingSessions();
          }
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadTrainingPackages() {
    this.loadingPackages = true;
    // Use getPackagesWithAvailability to get capacity-based availability
    this.trainingService.getPackagesWithAvailability({ event_id: this.eventId }).subscribe({
      next: (packages) => {
        this.trainingPackages = packages || [];
        // Debug logging for availability
        this.trainingPackages.forEach(pkg => {
          console.log(`Package: ${pkg.package_name}`, {
            is_available: pkg.is_available,
            availability_reason: pkg.availability_reason,
            slots_left: pkg.slots_left,
            current_registrations: pkg.current_registrations
          });
        });
        this.loadingPackages = false;
      },
      error: () => {
        this.loadingPackages = false;
      }
    });
  }

  loadTrainingSessions() {
    this.loadingSessions = true;
    this.trainingService.getPublicSessions({ event_id: this.eventId }).subscribe({
      next: (sessions) => {
        this.trainingSessions = (sessions || []).sort((a, b) => {
          // Sort by start_time
          const timeA = a.start_time ? new Date(a.start_time).getTime() : Infinity;
          const timeB = b.start_time ? new Date(b.start_time).getTime() : Infinity;
          return timeA - timeB;
        });
        this.loadingSessions = false;

        // Load session assignments for training events
        this.loadTrainingSessionAssignments();
      },
      error: () => {
        this.loadingSessions = false;
      }
    });
  }

  loadTrainingSessionAssignments() {
    this.trainingService.getPublicSessionAssignments({ event_id: this.eventId }).subscribe({
      next: (assignments) => {
        this.trainingSessionAssignments = assignments || [];
      },
      error: () => {
        this.trainingSessionAssignments = [];
      }
    });
  }

  // Group training sessions by date
  getTrainingSessionsByDate(): { date: string; sessions: TrainingSessionResponse[] }[] {
    const grouped = new Map<string, TrainingSessionResponse[]>();

    for (const session of this.trainingSessions) {
      let dateKey: string;
      if (session.start_time) {
        const date = new Date(session.start_time);
        dateKey = date.toISOString().split('T')[0];
      } else {
        dateKey = 'unscheduled';
      }

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(session);
    }

    return Array.from(grouped.entries())
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([date, sessions]) => ({ date, sessions }));
  }

  // Get participants for a training session
  getSessionParticipants(sessionId: number): { name: string; horse?: string }[] {
    const sessionAssignments = this.trainingSessionAssignments.filter(
      a => a.session_id === sessionId && a.status !== 'cancelled'
    );

    return sessionAssignments.map(a => {
      const reg = a.registration;
      if (!reg || !reg.user) {
        return { name: 'Unknown', horse: undefined };
      }
      const userName = `${reg.user.first_name || ''} ${reg.user.last_name || ''}`.trim() || 'Unknown';
      const horseName = reg.horse?.name || undefined;
      return { name: userName, horse: horseName };
    });
  }

  // Toggle training session expand
  toggleTrainingSessionExpand(sessionId: number): void {
    if (this.expandedTrainingSessions.has(sessionId)) {
      this.expandedTrainingSessions.delete(sessionId);
    } else {
      this.expandedTrainingSessions.add(sessionId);
    }
  }

  // Check if training session is expanded
  isTrainingSessionExpanded(sessionId: number): boolean {
    return this.expandedTrainingSessions.has(sessionId);
  }

  getSessionTypeIcon(type: string): string {
    switch (type) {
      case 'group': return 'fa-users';
      case 'individual': return 'fa-user';
      case 'break': return 'fa-mug-hot';
      default: return 'fa-calendar';
    }
  }

  getSessionTypeBadgeClass(type: string): string {
    switch (type) {
      case 'group': return 'badge-light-primary';
      case 'individual': return 'badge-light-success';
      case 'break': return 'badge-light-warning';
      default: return 'badge-light-secondary';
    }
  }

  formatSessionType(type: string): string {
    switch (type) {
      case 'group': return this.translateService.instant('eventDetails.sessionType.group');
      case 'individual': return this.translateService.instant('eventDetails.sessionType.individual');
      case 'break': return this.translateService.instant('eventDetails.sessionType.break');
      default: return type;
    }
  }

  async geocodeEventAddress() {
    if (!this.event) return;
    if (!this.event.address && !this.event.city && !this.event.country) return;

    this.geocodingInProgress = true;

    const result = await this.geocodingService.geocodeAddress(
      this.event.address || '',
      this.event.city || '',
      this.event.postal_code || '',
      this.event.country || ''
    );

    if (result.success && result.lat && result.lng) {
      this.mapMarker = {
        id: this.event.event_id,
        lat: result.lat,
        lng: result.lng,
        title: this.event.event_name || 'Event Location',
        subtitle: `${this.event.city || ''}${this.event.city && this.event.country ? ', ' : ''}${this.event.country || ''}`,
        imageUrl: this.event.header_url || undefined,
        data: this.event
      };
    }

    this.geocodingInProgress = false;
  }

  loadEventClasses() {
    this.loadingClasses = true;

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEventClasses(this.eventId).subscribe({
      next: (classes) => {
        this.eventClasses = classes || [];
        this.loadingClasses = false;
      },
      error: () => {
        this.loadingClasses = false;
      }
    });
  }

  loadEventServices() {
    this.loadingServices = true;

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEventServices(this.eventId).subscribe({
      next: (services) => {
        this.eventServices = services || [];
        this.loadingServices = false;
      },
      error: () => {
        this.loadingServices = false;
      }
    });
  }

  loadEventSponsors() {
    this.loadingSponsors = true;

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEventSponsors(this.eventId).subscribe({
      next: (sponsors) => {
        this.eventSponsors = sponsors || [];
        this.loadingSponsors = false;
      },
      error: () => {
        this.loadingSponsors = false;
      }
    });
  }

  loadEventContacts() {
    this.loadingContacts = true;

    // Use public endpoint (no auth required)
    this.eventsService.getPublicEventContacts(this.eventId).subscribe({
      next: (contacts) => {
        // Sort contacts by role_id with 0 first
        this.eventContacts = (contacts || []).sort((a, b) => {
          if (a.role_id === 0) return -1;
          if (b.role_id === 0) return 1;
          return a.role_id - b.role_id;
        });
        this.loadingContacts = false;
      },
      error: () => {
        this.loadingContacts = false;
      }
    });
  }

  loadAnnouncements() {
    this.loadingAnnouncements = true;
    this.eventsService.getPublicAnnouncements({ event_id: this.eventId }).subscribe({
      next: (announcements) => {
        this.announcements = announcements || [];
        this.loadingAnnouncements = false;
      },
      error: () => {
        this.loadingAnnouncements = false;
      }
    });
  }

  setView(view: 'information' | 'announcements' | 'results') {
    this.currentView = view;
    if (view === 'results' && this.classResults.length === 0) {
      this.loadResults();
    }
  }

  toggleDescription() {
    this.descriptionExpanded = !this.descriptionExpanded;
  }

  openLightbox(imageUrl: string) {
    this.lightboxImage = imageUrl;
  }

  closeLightbox() {
    this.lightboxImage = null;
  }

  formatAnnouncementDate(date: Date | string | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getGoogleMapsUrl(event: EventResponse): string {
    const addressParts = [
      event.address,
      event.postal_code,
      event.city,
      event.country
    ].filter(part => part);

    const fullAddress = addressParts.join(', ');
    const encodedAddress = encodeURIComponent(fullAddress);

    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  registerToEvent() {
    console.log('Navigating to event registration:', this.eventId);
    this.router.navigate(['/event-registration', this.eventId]);
  }

  openSendMessage() {
    const modalRef = this.modalService.open(SendMessageModalComponent, {
      size: 'md',
      centered: true
    });
    modalRef.componentInstance.event = this.event;
  }

  formatDateTime(dateTime: Date | string | null | undefined): string {
    if (!dateTime) return '';

    const date = dateTime instanceof Date ? dateTime : new Date(dateTime);

    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }

  getServiceAvailableSlots(service: EventServiceResponse): number {
    if (!service.max_num_registrations) return 0;
    // Sum up num_registrations from each registration, not just count records
    const registeredCount = service.event_service_registrations?.reduce(
      (sum, reg) => sum + (reg.num_registrations || 0), 0
    ) || 0;
    return Math.max(0, service.max_num_registrations - registeredCount);
  }

  getClassRegistrationCount(eventClass: EventClassResponse): number {
    return eventClass.event_class_registrations?.length || 0;
  }

  shouldAnimateSponsors(): boolean {
    return this.eventSponsors.length > 3;
  }

  loadPublicSchedules(isRefresh: boolean = false) {
    // Only show loading on initial load, not on refresh
    if (!isRefresh) {
      this.loadingSchedules = true;
      this.eventSchedules = [];
    }

    // Load all schedules for event using public endpoint (no auth required)
    const params: AQH_EVE_53 = {
      event_id: this.eventId
    };

    this.eventsService.getPublicSchedules(params).subscribe({
      next: (schedules) => {
        const newSchedules = schedules && Array.isArray(schedules) ? schedules : [];
        // Update schedules in place to avoid re-render flash
        if (isRefresh) {
          this.mergeSchedules(newSchedules);
        } else {
          this.eventSchedules = newSchedules;
        }
        this.loadingSchedules = false;
      },
      error: () => {
        if (!isRefresh) {
          this.eventSchedules = [];
        }
        this.loadingSchedules = false;
      }
    });
  }

  // Load entries for ALL schedules so participants can see the start list and know when to arrive
  private loadEntriesForSchedules() {
    for (const schedule of this.eventSchedules) {
      // Only load if entries not already present
      if (!schedule.entries || schedule.entries.length === 0) {
        this.eventsService.getScheduleEntries({ schedule_id: schedule.schedule_id }).subscribe({
          next: (entries) => {
            schedule.entries = entries || [];
          },
          error: () => {
            // Silently fail - entries just won't be available
          }
        });
      }
    }
  }

  // Merge new schedules into existing ones to avoid full re-render
  private mergeSchedules(newSchedules: EventScheduleResponse[]) {
    for (const newSchedule of newSchedules) {
      const existingIndex = this.eventSchedules.findIndex(s => s.schedule_id === newSchedule.schedule_id);
      if (existingIndex >= 0) {
        // Update existing schedule in place
        const existing = this.eventSchedules[existingIndex];
        existing.status = newSchedule.status;
        existing.current_entry_position = newSchedule.current_entry_position;
        existing.started_at = newSchedule.started_at;
        // Update entries in place
        if (newSchedule.entries) {
          if (!existing.entries) {
            existing.entries = newSchedule.entries;
          } else {
            for (const newEntry of newSchedule.entries) {
              const existingEntryIndex = existing.entries.findIndex(e => e.entry_id === newEntry.entry_id);
              if (existingEntryIndex >= 0) {
                // Update entry in place
                Object.assign(existing.entries[existingEntryIndex], newEntry);
              } else {
                existing.entries.push(newEntry);
              }
            }
          }
        }
      } else {
        // Add new schedule
        this.eventSchedules.push(newSchedule);
      }
    }

    // Remove schedules that no longer exist
    this.eventSchedules = this.eventSchedules.filter(existing =>
      newSchedules.some(newS => newS.schedule_id === existing.schedule_id)
    );
  }

  hasLiveSchedules(): boolean {
    return this.eventSchedules.some(s => s.status === 'live');
  }

  getLiveSchedules(): EventScheduleResponse[] {
    return this.eventSchedules.filter(s => s.status === 'live');
  }

  getCurrentEntry(schedule: EventScheduleResponse): EventScheduleEntryResponse | null {
    if (!schedule.entries) return null;
    const participants = this.getAllParticipants(schedule);
    const currentPos = schedule.current_entry_position || 1;
    // Find by in_progress status first, then by current_entry_position
    return schedule.entries.find(e => e.status === 'in_progress') ||
           participants.find(e => e.position === currentPos) ||
           null;
  }

  getNextEntry(schedule: EventScheduleResponse): EventScheduleEntryResponse | null {
    if (!schedule.entries) return null;
    const participants = this.getAllParticipants(schedule);
    const currentPos = schedule.current_entry_position || 1;
    // Find entries after current position
    return participants.find(e => e.position > currentPos && e.status !== 'scratched') || null;
  }

  getNextThreeEntries(schedule: EventScheduleResponse): EventScheduleEntryResponse[] {
    if (!schedule.entries) return [];
    const participants = this.getAllParticipants(schedule);
    const currentPos = schedule.current_entry_position || 1;
    return participants
      .filter(e => e.position > currentPos && e.status !== 'scratched')
      .slice(0, 3);
  }

  getAllParticipants(schedule: EventScheduleResponse): EventScheduleEntryResponse[] {
    if (!schedule.entries) return [];
    return schedule.entries
      .filter(e => e.entry_type === 'participant' && e.status !== 'scratched')
      .sort((a, b) => a.position - b.position);
  }

  // Participants + breaks in display order, for rendering the public start list
  getStartListEntries(schedule: EventScheduleResponse): EventScheduleEntryResponse[] {
    if (!schedule.entries) return [];
    return schedule.entries
      .filter(e => (e.entry_type === 'participant' || e.entry_type === 'break') && e.status !== 'scratched')
      .sort((a, b) => a.position - b.position);
  }

  formatBreakDuration(seconds: number | undefined | null): string {
    if (!seconds) return '';
    const minutes = Math.round(seconds / 60);
    return `${minutes} min`;
  }

  getEntryStatusClass(entry: EventScheduleEntryResponse, schedule: EventScheduleResponse): string {
    const currentPos = schedule.current_entry_position || 1;
    if (entry.status === 'completed' || entry.position < currentPos) return 'completed';
    if (entry.status === 'scratched') return 'scratched';
    if (entry.position === currentPos) return 'current';
    return 'pending';
  }

  getEntryDisplayName(entry: EventScheduleEntryResponse): string {
    if (entry.entry_type === 'break') {
      return entry.notes || this.translateService.instant('eventDetails.break');
    }
    if (!entry.registration) {
      return 'Unknown';
    }
    const reg = entry.registration;
    const userName = reg.user?.full_name || `${reg.user?.first_name || ''} ${reg.user?.last_name || ''}`.trim();
    const horseName = reg.horse?.name || '';
    return horseName ? `${userName} - ${horseName}` : userName;
  }

  getScheduleClassName(schedule: EventScheduleResponse): string {
    const eventClass = this.eventClasses.find(c => c.event_class_id === schedule.event_class_id);
    return eventClass?.class_name || 'Unknown Class';
  }

  getCompletedCount(schedule: EventScheduleResponse): number {
    return schedule.entries?.filter(e => e.status === 'completed').length || 0;
  }

  getTotalCount(schedule: EventScheduleResponse): number {
    return schedule.entries?.filter(e => e.entry_type === 'participant').length || 0;
  }

  // Timetable helper methods
  getClassesByDate(): { date: string; classes: EventClassResponse[] }[] {
    const grouped = new Map<string, EventClassResponse[]>();

    // Sort classes by start time first
    const sortedClasses = [...this.eventClasses].sort((a, b) => {
      const timeA = a.start_date_time ? new Date(a.start_date_time).getTime() : Infinity;
      const timeB = b.start_date_time ? new Date(b.start_date_time).getTime() : Infinity;
      return timeA - timeB;
    });

    for (const eventClass of sortedClasses) {
      let dateKey: string;
      if (eventClass.start_date_time) {
        const date = new Date(eventClass.start_date_time);
        dateKey = date.toISOString().split('T')[0];
      } else {
        dateKey = 'unscheduled';
      }

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(eventClass);
    }

    // Convert to array and sort by date
    return Array.from(grouped.entries())
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([date, classes]) => ({ date, classes }));
  }

  getSchedulesByDate(): { date: string; schedules: EventScheduleResponse[] }[] {
    const grouped = new Map<string, EventScheduleResponse[]>();

    // Sort schedules by start time first
    const sortedSchedules = [...this.eventSchedules].sort((a, b) => {
      const timeA = a.schedule_start_time ? new Date(a.schedule_start_time).getTime() : Infinity;
      const timeB = b.schedule_start_time ? new Date(b.schedule_start_time).getTime() : Infinity;
      return timeA - timeB;
    });

    for (const schedule of sortedSchedules) {
      let dateKey: string;
      if (schedule.schedule_start_time) {
        const date = new Date(schedule.schedule_start_time);
        dateKey = date.toISOString().split('T')[0];
      } else {
        dateKey = 'unscheduled';
      }

      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(schedule);
    }

    // Convert to array and sort by date
    return Array.from(grouped.entries())
      .sort((a, b) => {
        if (a[0] === 'unscheduled') return 1;
        if (b[0] === 'unscheduled') return -1;
        return a[0].localeCompare(b[0]);
      })
      .map(([date, schedules]) => ({ date, schedules }));
  }

  formatScheduleDate(dateKey: string): string {
    if (dateKey === 'unscheduled') {
      return this.translateService.instant('eventDetails.timeToBeAnnounced');
    }
    const date = new Date(dateKey + 'T00:00:00');
    const locale = this.translateService.currentLang === 'no' ? 'nb-NO' : 'en-GB';
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };
    return date.toLocaleDateString(locale, options);
  }

  formatTime(dateTime: Date | string | null | undefined): string {
    if (!dateTime) return '';
    const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }

  getScheduleTypeIcon(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') {
      return 'fa-users';
    }
    if (schedule.schedule_type === 'clinic') {
      return 'fa-user-clock';
    }
    return 'fa-list-ol';
  }

  getScheduleTypeLabel(schedule: EventScheduleResponse): string {
    if (schedule.session_type === 'group') {
      return this.translateService.instant('eventDetails.scheduleType.group');
    }
    if (schedule.schedule_type === 'clinic') {
      return this.translateService.instant('eventDetails.scheduleType.lesson');
    }
    return this.translateService.instant('eventDetails.scheduleType.startList');
  }

  formatDuration(seconds: number | undefined): string {
    if (!seconds) return '';
    if (seconds >= 3600) {
      const hours = Math.floor(seconds / 3600);
      const mins = Math.floor((seconds % 3600) / 60);
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${Math.floor(seconds / 60)} min`;
  }

  getEstimatedEndTime(schedule: EventScheduleResponse): string {
    if (!schedule.schedule_start_time || !schedule.time_per_entry_seconds) return '';
    const startTime = new Date(schedule.schedule_start_time);
    const totalParticipants = this.getTotalCount(schedule);
    const totalSeconds = totalParticipants * schedule.time_per_entry_seconds;
    const endTime = new Date(startTime.getTime() + totalSeconds * 1000);
    return this.formatTime(endTime);
  }

  // Group session helper methods
  getScheduleGroups(schedule: EventScheduleResponse): { groupId: number; name: string; entries: any[]; startTime?: any; endTime?: any; durationSeconds?: number }[] {
    // Use schedule.groups if available (new data structure)
    if (schedule.groups && schedule.groups.length > 0) {
      return schedule.groups.map(group => {
        // Find entries for this group
        const groupEntries = (schedule.entries || []).filter(e =>
          e.schedule_group_id === group.schedule_group_id && e.entry_type !== 'break'
        );

        return {
          groupId: group.schedule_group_id,
          name: group.group_name,
          entries: groupEntries,
          startTime: group.start_time,
          endTime: group.end_time,
          durationSeconds: group.duration_seconds ?? undefined
        };
      });
    }

    // Fallback: build groups from entries (legacy structure)
    if (!schedule.entries) return [];

    const groupMap = new Map<number, { name: string; entries: any[] }>();

    for (const entry of schedule.entries) {
      if (entry.entry_type === 'break') continue;
      const groupId = entry.schedule_group_id || entry.group_id;
      if (!groupId) continue;

      if (!groupMap.has(groupId)) {
        groupMap.set(groupId, {
          name: entry.lesson_name || `Group ${groupId}`,
          entries: []
        });
      }
      groupMap.get(groupId)!.entries.push(entry);
    }

    return Array.from(groupMap.entries()).map(([groupId, data]) => ({
      groupId,
      name: data.name,
      entries: data.entries
    }));
  }

  getScheduleBlocks(schedule: EventScheduleResponse): {
    type: 'group' | 'break';
    groupId?: number;
    name?: string;
    startTime?: Date | string | null;
    endTime?: Date | string | null;
    durationMinutes?: number;
    participantCount?: number;
    participants?: { entry_id: number; name: string; horse?: string }[];
    notes?: string;
  }[] {
    const blocks: any[] = [];
    const groups = this.getScheduleGroups(schedule);

    if (groups.length === 0) return [];

    // Sort groups by start time (use group's own startTime from new structure)
    const groupsWithTimes = groups.map(group => {
      // Use group's start/end time if available (new structure), otherwise fallback to first entry
      const firstEntry = group.entries[0];
      return {
        ...group,
        startTime: group.startTime || firstEntry?.start_time,
        endTime: group.endTime || firstEntry?.end_time,
        durationSeconds: group.durationSeconds || firstEntry?.duration_seconds
      };
    }).sort((a, b) => {
      if (!a.startTime) return 1;
      if (!b.startTime) return -1;
      return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
    });

    // Build blocks with groups and breaks
    for (let i = 0; i < groupsWithTimes.length; i++) {
      const group = groupsWithTimes[i];

      // Extract participant info from entries
      const participants = group.entries
        .filter((e: any) => e.registration)
        .map((e: any) => ({
          entry_id: e.entry_id,
          name: this.getEntryDisplayName(e),
          horse: e.registration?.horse?.name || null
        }));

      // Add group block
      blocks.push({
        type: 'group',
        groupId: group.groupId,
        name: group.name,
        startTime: group.startTime,
        endTime: group.endTime,
        durationMinutes: group.durationSeconds ? Math.floor(group.durationSeconds / 60) : 0,
        participantCount: group.entries.length,
        participants: participants
      });

      // Check for break between this group and the next
      if (i < groupsWithTimes.length - 1) {
        const nextGroup = groupsWithTimes[i + 1];
        if (group.endTime && nextGroup.startTime) {
          const endTime = new Date(group.endTime).getTime();
          const nextStartTime = new Date(nextGroup.startTime).getTime();
          const gapMinutes = Math.floor((nextStartTime - endTime) / 60000);

          if (gapMinutes > 0) {
            // Find break entry if it exists
            const breakEntry = schedule.entries?.find(e =>
              e.entry_type === 'break' &&
              e.start_time &&
              new Date(e.start_time).getTime() >= endTime &&
              new Date(e.start_time).getTime() < nextStartTime
            );

            blocks.push({
              type: 'break',
              durationMinutes: gapMinutes,
              notes: breakEntry?.notes || 'Break'
            });
          }
        }
      }
    }

    return blocks;
  }

  // Group expand/collapse for mobile
  isGroupExpanded(scheduleId: number, groupId: number | undefined): boolean {
    if (!groupId) return false;
    return this.expandedGroups.has(`${scheduleId}-${groupId}`);
  }

  toggleGroupExpand(scheduleId: number, groupId: number | undefined): void {
    if (!groupId) return;
    const key = `${scheduleId}-${groupId}`;
    if (this.expandedGroups.has(key)) {
      this.expandedGroups.delete(key);
    } else {
      this.expandedGroups.add(key);
    }
  }

  formatPackagePrice(priceCents: number, currency: string): string {
    const price = priceCents / 100;
    const formatted = price % 1 === 0 ? price.toString() : price.toFixed(2);
    return `${formatted} ${currency}`;
  }

  getSessionTypeLabel(type: string): string {
    switch (type) {
      case 'group': return this.translateService.instant('eventDetails.packageType.group');
      case 'individual': return this.translateService.instant('eventDetails.packageType.individual');
      case 'both': return this.translateService.instant('eventDetails.packageType.both');
      default: return type;
    }
  }

  // Results methods
  loadResults() {
    this.loadingResults = true;
    this.eventsService.getPublicResults(this.eventId).subscribe({
      next: (results) => {
        this.classResults = results || [];
        // Expand first class by default if there are results
        if (this.classResults.length > 0) {
          this.expandedClassResults.add(this.classResults[0].event_class_id);
        }
        this.loadingResults = false;
      },
      error: () => {
        this.classResults = [];
        this.loadingResults = false;
      }
    });
  }

  toggleClassResults(classId: number) {
    const newSet = new Set(this.expandedClassResults);
    if (newSet.has(classId)) {
      newSet.delete(classId);
    } else {
      newSet.add(classId);
    }
    this.expandedClassResults = newSet;
  }

  getResultScore(result: any): string {
    if (result.final_time_seconds || result.raw_time_seconds) {
      return this.formatResultTime(result.final_time_seconds || result.raw_time_seconds);
    } else if (result.final_points !== null && result.final_points !== undefined) {
      return `${result.final_points} pts`;
    } else if (result.raw_points !== null && result.raw_points !== undefined) {
      return `${result.raw_points} pts`;
    } else if (result.status === 'dnf') {
      return 'DNF';
    } else if (result.status === 'dns') {
      return 'DNS';
    } else if (result.status === 'dq') {
      return 'DQ';
    }
    return '-';
  }

  formatResultTime(seconds: number | undefined | null): string {
    if (!seconds && seconds !== 0) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const wholeSecs = Math.floor(secs);
    const ms = Math.round((secs - wholeSecs) * 1000);

    if (mins > 0) {
      return `${mins}:${wholeSecs.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    }
    return `${wholeSecs}.${ms.toString().padStart(3, '0')}`;
  }

  viewResultImages(result: PublicResultResponse, event: Event) {
    event.stopPropagation();
    this.scoringService.getResultImages({ result_id: result.result_id }).subscribe({
      next: (images) => {
        if (images && images.length > 0) {
          const modalRef = this.modalService.open(DocumentViewerModal, {
            size: 'lg',
            centered: true
          });
          modalRef.componentInstance.images = images;
          modalRef.componentInstance.title = this.translateService.instant('images.resultImages');
        }
      }
    });
  }

  viewRoundResultImages(roundResult: PublicRoundResultResponse, event: Event) {
    event.stopPropagation();
    this.scoringService.getResultImages({ result_id: roundResult.result_id }).subscribe({
      next: (images) => {
        if (images && images.length > 0) {
          const modalRef = this.modalService.open(DocumentViewerModal, {
            size: 'lg',
            centered: true
          });
          modalRef.componentInstance.images = images;
          const roundLabel = roundResult.round_name || `${this.translateService.instant('eventDetails.round')} ${roundResult.round_number}`;
          modalRef.componentInstance.title = `${this.translateService.instant('images.resultImages')} - ${roundLabel}`;
        }
      }
    });
  }

  hasMultipleRounds(classResult: PublicClassResultsResponse): boolean {
    // Check if ANY result has round_results (not just the first one)
    return classResult.results?.some(r => r.round_results && r.round_results.length > 0) || false;
  }

  getRoundHeaders(classResult: PublicClassResultsResponse): PublicRoundResultResponse[] {
    // Find the first result that has round_results to use for headers
    const resultWithRounds = classResult.results?.find(r => r.round_results && r.round_results.length > 0);
    return resultWithRounds?.round_results || [];
  }

  navigateToOrganisation() {
    if (this.event?.company?.company_id) {
      this.router.navigate(['/organisations', this.event.company.company_id]);
    }
  }

  // ============================================================================
  // MEMBERSHIP PRICING METHODS
  // ============================================================================

  loadUserPrices(): void {
    this.loadingPrices = true;
    this.eventsService.getUserEventPrices({ event_id: this.eventId }).subscribe({
      next: (response: UserPricesResponse) => {
        this.userPlanIds = response.user_plan_ids || [];
        this.priceInfoMap.clear();

        // Build price info map keyed by "type-id"
        for (const priceInfo of response.prices) {
          const key = `${priceInfo.item_type}-${priceInfo.item_id}`;
          this.priceInfoMap.set(key, priceInfo);
        }

        this.loadingPrices = false;
      },
      error: () => {
        this.loadingPrices = false;
      }
    });
  }

  getPriceInfo(itemType: 'class' | 'package' | 'service', itemId: number): PriceInfoResponse | null {
    const key = `${itemType}-${itemId}`;
    return this.priceInfoMap.get(key) || null;
  }

  getEffectivePrice(itemType: 'class' | 'package' | 'service', itemId: number, basePrice: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo) return basePrice;
    // For packages, effective_price is in cents - convert to currency units
    return itemType === 'package' ? priceInfo.effective_price / 100 : priceInfo.effective_price;
  }

  hasMemberDiscount(itemType: 'class' | 'package' | 'service', itemId: number): boolean {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo !== null && priceInfo.applied_membership !== null && priceInfo.savings_percent > 0;
  }

  getSavingsPercent(itemType: 'class' | 'package' | 'service', itemId: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo?.savings_percent || 0;
  }

  canGetMemberDiscount(itemType: 'class' | 'package' | 'service', itemId: number): boolean {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    return priceInfo !== null &&
           priceInfo.applied_membership === null &&
           priceInfo.purchasable_plans.length > 0;
  }

  getBestMemberPrice(itemType: 'class' | 'package' | 'service', itemId: number): number | null {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return null;
    }
    // Find the lowest discounted price among all purchasable plans
    const bestPlan = priceInfo.purchasable_plans.reduce((best, current) =>
      current.discounted_price < best.discounted_price ? current : best
    );
    // For packages, prices are in øre (cents), convert to kroner
    const price = bestPlan.discounted_price;
    return itemType === 'package' ? price / 100 : price;
  }

  getBestMemberSavings(itemType: 'class' | 'package' | 'service', itemId: number): number {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return 0;
    }
    // Find the highest savings percent among all purchasable plans
    const bestPlan = priceInfo.purchasable_plans.reduce((best, current) =>
      current.savings_percent > best.savings_percent ? current : best
    );
    return bestPlan.savings_percent;
  }

  openMembershipPurchaseModal(itemType: 'class' | 'package' | 'service', itemId: number, itemName: string, basePrice: number, currency: string): void {
    const priceInfo = this.getPriceInfo(itemType, itemId);
    if (!priceInfo || priceInfo.purchasable_plans.length === 0) {
      return;
    }

    // For packages, convert discounted_price from øre to kroner
    const purchasablePlans = itemType === 'package'
      ? priceInfo.purchasable_plans.map(plan => ({
          ...plan,
          discounted_price: plan.discounted_price / 100
        }))
      : priceInfo.purchasable_plans;

    const modalRef = this.modalService.open(MembershipPurchaseModal, {
      size: 'lg',
      centered: true
    });

    modalRef.componentInstance.itemName = itemName;
    modalRef.componentInstance.basePrice = basePrice;
    modalRef.componentInstance.currency = currency;
    modalRef.componentInstance.purchasablePlans = purchasablePlans;
    modalRef.componentInstance.eventId = this.eventId;
    modalRef.componentInstance.returnUrl = `/event-details/${this.eventId}`;

    modalRef.result.then(
      (result) => {
        if (result?.action === 'purchase') {
          console.log('User selected membership plan:', result.plan);
        }
      },
      () => {
        // Modal dismissed
      }
    );
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
